import { ref, computed, watch, type Ref, type ComputedRef } from 'vue';
import { wageMagamApi } from '@/services/api';

type UseWageMagamOptions = {
  memberCode: Ref<string> | ComputedRef<string>;
  yearMonth: Ref<string> | ComputedRef<string>;
  bizGubun: Ref<string> | ComputedRef<string>;
  /** 전월에 임금 데이터가 있으면 true — 있으면 전월 마감 필수 */
  hasPrevWageData?: () => Promise<boolean>;
};

/** YYYYMM → 전월 YYYYMM */
export const getPrevYearMonth = (yearMonth: string): string => {
  const ym = String(yearMonth || '');
  const y = parseInt(ym.slice(0, 4), 10);
  const m = parseInt(ym.slice(4, 6), 10);
  if (!y || !m) return '';
  if (m === 1) return `${y - 1}12`;
  return `${y}${String(m - 1).padStart(2, '0')}`;
};

export const formatYearMonthLabel = (yearMonth: string): string => {
  const ym = String(yearMonth || '');
  if (ym.length < 6) return ym;
  return `${ym.slice(0, 4)}년 ${parseInt(ym.slice(4, 6), 10)}월`;
};

/** 센터장용 — 전월 미마감 안내 */
export const buildPrevMonthNotClosedGuide = (prevYm: string): string => {
  const label = formatYearMonthLabel(prevYm);
  return (
    `${label} 임금을 아직 마감하지 않았습니다.\n\n` +
    `지난달 임금을 먼저 마감해야 이번 달 임금을 작성할 수 있어요.\n\n` +
    `① 화면 위 연월을 ${label}로 바꾸기\n` +
    `② 「마감하기」 누르기\n` +
    `③ 다시 이번 달로 돌아와 작성하기`
  );
};

const formatGapNames = (names: string[] = []): string => {
  const shown = names.slice(0, 10).join(', ');
  const more = names.length > 10 ? ` 외 ${names.length - 10}명` : '';
  return shown ? `: ${shown}${more}` : '';
};

/** 센터장용 — 주민번호·입사일 미등록으로 마감 불가 안내 */
export const buildProfileRequiredForMagamGuide = (
  ssnCount: number,
  ssnNames: string[] = [],
  joinCount = 0,
  joinNames: string[] = []
): string => {
  const lines: string[] = [];
  if (ssnCount > 0) lines.push(`주민번호 미등록 ${ssnCount}명${formatGapNames(ssnNames)}`);
  if (joinCount > 0) lines.push(`입사일 없음 ${joinCount}명${formatGapNames(joinNames)}`);
  return (
    `아래 항목이 비어 있어 마감할 수 없습니다.\n\n` +
    `${lines.join('\n')}\n\n` +
    `임금신고 때 사원 주민번호와 입사일이 꼭 필요해요.\n` +
    `마감 창에서 바로 등록한 뒤 다시 「마감하기」를 눌러 주세요.`
  );
};

/** 마감 버튼 툴팁 — 비어 있는 항목이 없으면 기본 문구 */
export const buildMagamBlockButtonTitle = (ssnCount: number, joinCount: number): string => {
  const parts: string[] = [];
  if (ssnCount > 0) parts.push(`주민번호 미등록 ${ssnCount}명`);
  if (joinCount > 0) parts.push(`입사일 없음 ${joinCount}명`);
  if (!parts.length) return '해당 연월 임금을 마감합니다';
  return `${parts.join(' · ')} — 눌러서 바로 등록할 수 있습니다`;
};

export function useWageMagam({
  memberCode,
  yearMonth,
  bizGubun,
  hasPrevWageData,
}: UseWageMagamOptions) {
  const isClosed = ref(false);
  /** 이 연월의 마감 조회가 끝났을 때만 isClosed를 쓴다. 이전 달 마감이 넘어오지 않게 */
  const resolvedYearMonth = ref('');
  const closedAt = ref<string | null>(null);
  const closedBy = ref<string | null>(null);
  const memo = ref<string | null>(null);
  const loading = ref(false);
  const closing = ref(false);
  const releasing = ref(false);

  const prevYearMonth = ref('');
  const prevIsClosed = ref(false);
  const prevHasData = ref(false);

  /** 현원 중 주민번호·입사일 미등록 (마감 차단용) */
  const ssnMissingCount = ref(0);
  const ssnMissingNames = ref<string[]>([]);
  const joinMissingCount = ref(0);
  const joinMissingNames = ref<string[]>([]);

  /** 전월 데이터가 있는데 마감 안 됨 → 당월 작성 차단 */
  const prevMonthNeedsClose = computed(
    () => !!prevYearMonth.value && prevHasData.value && !prevIsClosed.value
  );

  const ssnMissingBlocksClose = computed(
    () => !isClosed.value && (ssnMissingCount.value > 0 || joinMissingCount.value > 0)
  );

  const isReadOnly = computed(() => isClosed.value);

  const prevMonthGuideMessage = computed(() => {
    if (!prevMonthNeedsClose.value || !prevYearMonth.value) return '';
    return buildPrevMonthNotClosedGuide(prevYearMonth.value);
  });

  const ssnMagamGuideMessage = computed(() => {
    if (!ssnMissingBlocksClose.value) return '';
    return buildProfileRequiredForMagamGuide(
      ssnMissingCount.value,
      ssnMissingNames.value,
      joinMissingCount.value,
      joinMissingNames.value
    );
  });

  let loadSeq = 0;

  const loadStatus = async () => {
    const requestedYm = yearMonth.value;
    const seq = ++loadSeq;
    if (!memberCode.value || !requestedYm || !bizGubun.value) {
      isClosed.value = false;
      resolvedYearMonth.value = requestedYm;
      closedAt.value = null;
      closedBy.value = null;
      memo.value = null;
      prevYearMonth.value = '';
      prevIsClosed.value = false;
      prevHasData.value = false;
      ssnMissingCount.value = 0;
      ssnMissingNames.value = [];
      joinMissingCount.value = 0;
      joinMissingNames.value = [];
      return;
    }

    if (resolvedYearMonth.value !== requestedYm) {
      resolvedYearMonth.value = '';
    }

    loading.value = true;
    try {
      const prevYm = getPrevYearMonth(requestedYm);
      prevYearMonth.value = prevYm;

      const [currRes, prevRes, hasData] = await Promise.all([
        wageMagamApi.getStatus({
          mCode: memberCode.value,
          yearMonth: requestedYm,
          bizGubun: bizGubun.value,
        }),
        prevYm
          ? wageMagamApi.getStatus({
              mCode: memberCode.value,
              yearMonth: prevYm,
              bizGubun: bizGubun.value,
            })
          : Promise.resolve(null),
        hasPrevWageData && prevYm
          ? hasPrevWageData().catch(() => false)
          : Promise.resolve(false),
      ]);

      if (seq !== loadSeq || yearMonth.value !== requestedYm) return;

      if (currRes.data?.success) {
        isClosed.value = !!currRes.data.data?.isClosed;
        closedAt.value = currRes.data.data?.closedAt || null;
        closedBy.value = currRes.data.data?.closedBy || null;
        memo.value = currRes.data.data?.memo || null;
        ssnMissingCount.value = Number(currRes.data.data?.ssnMissingCount || 0);
        ssnMissingNames.value = Array.isArray(currRes.data.data?.ssnMissingNames)
          ? currRes.data.data.ssnMissingNames
          : [];
        joinMissingCount.value = Number(currRes.data.data?.joinMissingCount || 0);
        joinMissingNames.value = Array.isArray(currRes.data.data?.joinMissingNames)
          ? currRes.data.data.joinMissingNames
          : [];
      } else {
        isClosed.value = false;
        closedAt.value = null;
        closedBy.value = null;
        memo.value = null;
        ssnMissingCount.value = 0;
        ssnMissingNames.value = [];
        joinMissingCount.value = 0;
        joinMissingNames.value = [];
      }

      prevIsClosed.value = !!(prevRes?.data?.success && prevRes.data.data?.isClosed);
      prevHasData.value = !!hasData;
      resolvedYearMonth.value = requestedYm;
    } catch (error: any) {
      if (seq !== loadSeq || yearMonth.value !== requestedYm) return;
      const message = error.response?.data?.message || error.message || '알 수 없는 오류';
      const status = error.response?.status || '';
      alert(`임금 마감 상태 조회 실패: ${message}${status ? ` (${status})` : ''}`);
      isClosed.value = false;
      closedAt.value = null;
      closedBy.value = null;
      memo.value = null;
      resolvedYearMonth.value = requestedYm;
      prevIsClosed.value = false;
      prevHasData.value = false;
      ssnMissingCount.value = 0;
      ssnMissingNames.value = [];
      joinMissingCount.value = 0;
      joinMissingNames.value = [];
    } finally {
      if (seq === loadSeq) loading.value = false;
    }
  };

  const alertIfPrevMonthBlocked = (): boolean => {
    if (!prevMonthNeedsClose.value) return false;
    alert(prevMonthGuideMessage.value);
    return true;
  };

  const alertIfSsnBlocksClose = (): boolean => {
    if (!ssnMissingBlocksClose.value) return false;
    alert(ssnMagamGuideMessage.value);
    return true;
  };

  const closeMagam = async () => {
    if (!memberCode.value || !yearMonth.value || !bizGubun.value) {
      alert('시설·연월·사업구분 정보가 없습니다.');
      return false;
    }
    if (alertIfSsnBlocksClose()) return false;

    const label = formatYearMonthLabel(yearMonth.value);
    if (
      !window.confirm(
        `${label} 임금을 마감하시겠습니까?\n\n` +
          `마감 후에는 이 달 임금을 수정할 수 없습니다.\n` +
          `마감하면 임금명세서를 발송할 수 있습니다.\n\n` +
          `※ 임금신고를 위해 현원 직원의 주민번호와 입사일이 모두 등록되어 있어야 합니다.`
      )
    ) {
      return false;
    }

    closing.value = true;
    try {
      const response = await wageMagamApi.close({
        mCode: memberCode.value,
        yearMonth: yearMonth.value,
        bizGubun: bizGubun.value,
      });
      if (!response.data?.success) {
        throw new Error(response.data?.message || '마감 실패');
      }
      await loadStatus();
      alert(
        `${label} 임금 마감이 완료되었습니다.\n\n` +
          `이제 임금명세서를 발송할 수 있습니다.\n` +
          `목록 위 「임금명세서발송」버튼을 눌러 요양보호사 앱으로 보내 주세요.`
      );
      return true;
    } catch (error: any) {
      const message = error.response?.data?.message || error.message || '알 수 없는 오류';
      const status = error.response?.status || '';
      alert(`임금 마감 실패: ${message}${status ? ` (${status})` : ''}`);
      // 서버에서 최신 미등록 수 반영
      await loadStatus();
      return false;
    } finally {
      closing.value = false;
    }
  };

  const releaseMagam = async () => {
    if (!memberCode.value || !yearMonth.value || !bizGubun.value) {
      alert('시설·연월·사업구분 정보가 없습니다.');
      return false;
    }
    if (
      !window.confirm(
        '임금 마감을 해제하시겠습니까?\n\n' +
          '해제하면 다시 수정할 수 있습니다.\n' +
          '수정이 끝나면 다시 「마감하기」를 눌러 주세요.'
      )
    ) {
      return false;
    }

    releasing.value = true;
    try {
      const response = await wageMagamApi.release({
        mCode: memberCode.value,
        yearMonth: yearMonth.value,
        bizGubun: bizGubun.value,
      });
      if (!response.data?.success) {
        throw new Error(response.data?.message || '마감 해제 실패');
      }
      await loadStatus();
      alert('임금 마감이 해제되었습니다. 수정하신 뒤 다시 마감해 주세요.');
      return true;
    } catch (error: any) {
      const message = error.response?.data?.message || error.message || '알 수 없는 오류';
      const status = error.response?.status || '';
      alert(`임금 마감 해제 실패: ${message}${status ? ` (${status})` : ''}`);
      return false;
    } finally {
      releasing.value = false;
    }
  };

  watch([memberCode, yearMonth, bizGubun], () => {
    loadStatus();
  }, { immediate: true });

  return {
    isClosed,
    resolvedYearMonth,
    closedAt,
    closedBy,
    memo,
    loading,
    closing,
    releasing,
    isReadOnly,
    prevYearMonth,
    prevIsClosed,
    prevHasData,
    prevMonthNeedsClose,
    prevMonthGuideMessage,
    ssnMissingCount,
    ssnMissingNames,
    joinMissingCount,
    joinMissingNames,
    ssnMissingBlocksClose,
    ssnMagamGuideMessage,
    alertIfPrevMonthBlocked,
    alertIfSsnBlocksClose,
    loadStatus,
    closeMagam,
    releaseMagam,
  };
}
